import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, LogOut, BarChart3, Plus, FileText, Map, Building2, TrendingUp } from "lucide-react";
import { useUserStore } from "../store/userStore";
import Button from "../components/shared/Button";

export default function Navigation({ currentTab, onTabChange }) {
  const { role, user, setRole } = useUserStore();
  const [isOpen, setIsOpen] = useState(false);

  const presidenteTabs = [
    { id: "dashboard", label: "Mi Panel", icon: BarChart3 },
    { id: "nueva-solicitud", label: "Nueva Solicitud", icon: Plus },
    { id: "mis-solicitudes", label: "Mis Solicitudes", icon: FileText },
    { id: "mapa", label: "Mapa de mi Zona", icon: Map }
  ];

  const alcaldeTabs = [
    { id: "dashboard", label: "Centro de Control", icon: BarChart3 },
    { id: "solicitudes", label: "Solicitudes", icon: FileText },
    { id: "mapa-territorial", label: "Mapa Territorial", icon: Map },
    { id: "areas", label: "Áreas Municipales", icon: Building2 },
    { id: "reportes", label: "Reportes", icon: TrendingUp }
  ];

  const tabs = role === "presidente" ? presidenteTabs : alcaldeTabs;

  const handleTabChange = (tabId) => {
    onTabChange(tabId);
    setIsOpen(false);
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-2.5 rounded-xl">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Despacho Digital</h1>
              <p className="text-xs text-gray-500">Alcalde Darwin Sarmiento</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col items-end">
              <p className="text-sm font-semibold text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-500 capitalize">
                {role === "presidente" ? "Presidente de Barrio" : "Alcaldía"}
              </p>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setRole(role === "presidente" ? "alcalde" : "presidente")}
                className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors hidden md:block text-nowrap"
              >
                {role === "presidente" ? "Ver como Alcalde" : "Ver como Presidente de Barrio"}
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors md:hidden">
                {isOpen ? (
                  <X size={24} onClick={() => setIsOpen(false)} />
                ) : (
                  <Menu size={24} onClick={() => setIsOpen(true)} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-1 border-t border-gray-100 pt-4 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;
            
            return (
              <motion.button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                whileHover={{ backgroundColor: "rgb(243, 244, 246)" }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                  isActive
                    ? "bg-blue-100 text-blue-700 border-b-2 border-blue-600"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </motion.button>
            );
          })}
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden flex flex-col gap-2 border-t border-gray-100 pt-4 mt-4"
          >
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = currentTab === tab.id;
              
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 w-full justify-start transition-colors ${
                    isActive
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </motion.button>
              );
            })}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setRole(role === "presidente" ? "alcalde" : "presidente")}
                className="w-full"
              >
                {role === "presidente" ? "Ver como Alcalde" : "Ver como Presidente de Barrio"}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
}

